import type { components } from '../../lib/api/schema.gen'
import { apexAutomationGantry } from '../../lib/fixtures'

export { displayNameFor, findSourceableById } from '../../lib/fixtures/sourceable-lookup'

type Sourceable = components['schemas']['Sourceable']
type SourceableRef = components['schemas']['SourceableRef']
type Quantity = components['schemas']['Quantity']
type AddCartLineRequest = components['schemas']['AddCartLineRequest']
type Price = components['schemas']['Price']
type DeliveryPromise = components['schemas']['DeliveryPromise']

/**
 * The provenance a cart line's `price`/`deliveryPromise` legitimately comes
 * from: an accepted Quote (DM§9 / ADR-0030). This is the ONLY case where the
 * client sends a `price`/`deliveryPromise` on a `POST /cart/lines` — those
 * values were negotiated and accepted, so they carry real provenance
 * (`sourceQuoteId`/`sourceRfqId`) the BFF can verify. A plain catalog or
 * quote-required add never carries a client price (see `toAddCartLineRequest`).
 */
export interface AcceptedQuoteContext {
  readonly price: Price
  readonly deliveryPromise: DeliveryPromise
  readonly sourceQuoteId: string
  readonly sourceRfqId?: string
}

/**
 * UID-59 gap, flagged for follow-up: the add-row / CSV-import flows need to
 * resolve a typed SKU to a real Sourceable (id/kind/uom/price) before
 * POSTing `/cart/lines` — the Cart BFF itself has no such lookup (it only
 * ever receives a fully-formed `AddCartLineRequest`; UID-58 routes.ts). WS-2
 * (Catalog) doesn't have a search endpoint live yet either (screens.md §4 is
 * still a placeholder route). Until one of those ships, this resolves
 * against the UID-68 fixtures dataset — the same "demo dataset instead of
 * another team's runtime" seam every WS-2..5 vertical already builds
 * against. Recommend a follow-up ticket: a real `GET /catalog/search?sku=`
 * (or similar) the add-row/CSV-import UI can call instead.
 */
const CATALOG_BY_SKU = new Map<string, Sourceable>(
  [
    ...apexAutomationGantry.catalogSourceables,
    ...apexAutomationGantry.extraCatalogSourceables,
    // ADR-0128 — the curated "Recently viewed" row's parts must be
    // resolvable here too, or a SKU a shopper can see and click in that
    // row would 404 out of the add-row/CSV-import SKU lookup, an
    // incoherence ADR-0128 Decision 2 calls out explicitly.
    ...apexAutomationGantry.recentlyViewedCatalogSourceables,
  ]
    .filter((s): s is Sourceable & { catalogSku: string } => Boolean(s.catalogSku))
    .map((s) => [s.catalogSku.toUpperCase(), s]),
)

/** Look up a catalog Sourceable by its SKU (case-insensitive), for the
 * add-row / CSV-import flows. Undefined when the SKU has no fixture match. */
export function findCatalogSourceableBySku(sku: string): Sourceable | undefined {
  return CATALOG_BY_SKU.get(sku.trim().toUpperCase())
}

function toSourceableRef(sourceable: Sourceable): SourceableRef {
  return {
    id: sourceable.id,
    kind: sourceable.kind,
    uom: sourceable.uom,
    ...(sourceable.accountId !== undefined ? { accountId: sourceable.accountId } : {}),
    // UID-282: carry the custom-part revision label so a line added FROM a quote row
    // keeps the `Rev N` the RFQ was showing. Same rationale as the `displayName` /
    // `partNumber` labels below — this projection is field-by-field, so an unnamed
    // field is a silently dropped one.
    ...(sourceable.rev !== undefined ? { rev: sourceable.rev } : {}),
  }
}

/**
 * UID-424 fallback label — the RFQ line's OWN `part.name`/`part.partNumber`
 * (`RfqLineView.part`, see `rfq/domain.ts`), supplied by the RFQ→cart add
 * call sites so the cart-line label survives even when `findSourceableById`
 * has no fixture-catalog match for the line's sourceable (a real, non-demo
 * part). Independent of fixture-catalog coverage: the resolved `Sourceable`'s
 * own `name`/`catalogSku` still wins when present (no regression for catalog
 * parts) — this only fills the gap a bare, label-less `SourceableRef`
 * fallback would otherwise leave, which used to surface as the raw sourceable
 * UUID on the cart (UID-424).
 */
export interface FallbackLabel {
  readonly name?: string | undefined
  readonly partNumber?: string | undefined
}

/**
 * Build the `POST /cart/lines` request body for a resolved Sourceable +
 * requested quantity.
 *
 * C1 (Option C): a catalog or quote-required add sends ONLY
 * `{ sourceable, quantity }` — never a client-asserted `price`/
 * `deliveryPromise`. The BFF now resolves catalog price server-side via the
 * InstantQuoter ACL and DERIVES the line's `status` from Price+DeliveryPromise
 * presence (ADR-0004); an unprovenanced client-sent price is rejected 422 by
 * the guard (all-or-nothing price CHECK, W2/W3). So a catalog item lands
 * `ready` because the server priced it, and a custom/quote-required part lands
 * `pending` — the client asserts neither price nor status.
 *
 * The ONLY case a client legitimately sends `price`/`deliveryPromise` is an
 * accepted-quote line: those values were negotiated and accepted, so they
 * carry real provenance (`sourceQuoteId`, optional `sourceRfqId`) the BFF can
 * verify (DM§9 / ADR-0030). Pass an `AcceptedQuoteContext` for that path; omit
 * it for every catalog / quote-required / custom add.
 */
export function toAddCartLineRequest(
  sourceable: Sourceable,
  quantity: Quantity,
  acceptedQuote?: AcceptedQuoteContext,
  fallbackLabel?: FallbackLabel,
): AddCartLineRequest {
  const ref = toSourceableRef(sourceable)
  // UID-60 ("option 2"): carry the human part labels from the resolved Sourceable so the
  // BFF PERSISTS them on the cart line and the cart shows "<name> (<sku>)" instead of the
  // raw sourceable id. Sourced from the same fixture fields `displayNameFor` reads; omit a
  // label the Sourceable doesn't carry (e.g. a custom part has no catalogSku).
  //
  // UID-424: when the resolved Sourceable itself carries no name/sku (the
  // fixture-catalog miss this ticket fixes), fall back to the caller-supplied
  // `fallbackLabel` — the RFQ line's OWN `part.name`/`part.partNumber` — so a
  // real part the demo fixtures don't know about still ends up with a label
  // instead of degrading to the raw sourceable id.
  const name = sourceable.name ?? fallbackLabel?.name
  const partNumber = sourceable.catalogSku ?? fallbackLabel?.partNumber
  const labels = {
    ...(name != null ? { displayName: name } : {}),
    ...(partNumber != null ? { partNumber } : {}),
  }
  if (acceptedQuote) {
    return {
      sourceable: ref,
      quantity,
      price: acceptedQuote.price,
      deliveryPromise: acceptedQuote.deliveryPromise,
      sourceQuoteId: acceptedQuote.sourceQuoteId,
      ...(acceptedQuote.sourceRfqId !== undefined
        ? { sourceRfqId: acceptedQuote.sourceRfqId }
        : {}),
      ...labels,
    }
  }
  return { sourceable: ref, quantity, ...labels }
}
